import { dialog } from "nq-component";
import BaseListPresenter from "../../../../../base/BaseListPresenter";
import DialogModal from "../../../../../components/Dialog";
import {
  format,
  startOfDay,
  addHours,
  addMinutes,
  addDays,
  parseISO,
} from "date-fns";
import { getCurrentUserUseCase } from "../../../../../usecases/user";

import { s } from "framer-motion/client";
import { fmtDB, getPHWindow_5to459 } from "./utils/datePH";

class WasteProductPresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase
  ) {
    super(view, findObjectUseCase, deleteObjectUseCase);
    this.findObjectUseCase = findObjectUseCase;
    this.countObjectUseCase = countObjectUseCase;
    this.deleteObjectUseCase = deleteObjectUseCase;
    this.upsertUseCase = upsertUseCase;
  }

  init() {
    const user = this.view.getCurrentUser();
    const hasAdminRole = user?.roles?.some((role) => role.id === "Admin");
    const storeId = user?.storeAnalytic?.[0]?.id;
    this.where = hasAdminRole ? {} : { "storeId.id": { $eq: storeId } };
    const now = Date();

    // Set to 5:00 AM of today
    const startOfBusinessDay = new Date(now);
    startOfBusinessDay.setHours(5, 0, 0, 0);

    // Set to 4:59:59.999 AM of tomorrow
    const endOfBusinessDay = new Date(now);
    endOfBusinessDay.setDate(endOfBusinessDay.getDate() + 1);
    endOfBusinessDay.setHours(4, 59, 59, 999);

    console.log("startOfBusinessDay", startOfBusinessDay);
    console.log("endOfBusinessDay", endOfBusinessDay);

    this.where = hasAdminRole
      ? {
          category: "product",
          isUpsale: false,
          tran_date: {
            $gte: startOfBusinessDay,
            $lte: endOfBusinessDay,
          },
        }
      : {
          "storeId.id": { $eq: storeId },
          category: "product",
          isUpsale: false,
          tran_date: {
            $gte: startOfBusinessDay,
            $lte: endOfBusinessDay,
          },
        };

    this.limit = 20;
    this.search = {};
    this.filter = {};
    this.include = ["all"];
    this.keys = undefined;
    this.sort = { created: -1 };
    this.progress = true;
    this.reset();
  }

  async getObjects() {
    this.reset();
    await this.countObjects();
    await this.findObjects();
    await this.findTransactions();
  }

  createQuery() {
    const skip = (this.current - 1) * this.limit;
    const query = {
      limit: this.limit,
      skip: skip,
      where: {
        ...this.where,
        ...this.search,
        ...this.filter,
        ...this.filterDate,
      },
      include: this.include,
      count: true,
    };
    if (this.sort) {
      query.sort = this.sort;
    }
    // const keys = this.keys || this.view.getKeys() || [];
    // if (keys.length > 0) {
    //   query.keys = keys;
    // }
    return query;
  }

  async countObjects() {
    try {
      this.showProgress();
      const collection = this.view.getCollectionName();
      const query = this.createQuery();
      const { count } = await this.countObjectUseCase.execute(collection, {
        where: query.where,
      });
      this.count = count;
      this.view.setCount(this.count);
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
  }

  async findTransactions() {
    this.view.showProgressDialog();

    const collection = "transactions";
    const userLoggedIn = this.view.getCurrentUser();
    const user = await getCurrentUserUseCase().execute();

    // ---------- helpers ----------
    const safeStr = (v) => (v == null ? "" : String(v).trim());
    const num = (v, d = 0) => {
      if (v == null) return d;
      const n = Number(String(v).replace(/[^0-9.\-]/g, ""));
      return Number.isFinite(n) ? n : d;
    };
    const toMoney = (n) => num(n).toFixed(2);

    // ---------- 1) Fetch user + stores ----------
    const storeQuery = { include: ["all"], where: { email: user.email } };
    this.store = await this.findObjectUseCase.execute("users", storeQuery);

    const storeAnalytic = (this.store?.[0]?.storeAnalytic ?? []).sort((a, b) =>
      a.name.toUpperCase().localeCompare(b.name.toUpperCase())
    );

    const storeCodes = storeAnalytic.map((s) => s?.name).filter(Boolean);
    if (!storeCodes.length) return [];

    // Map store_code -> storeAnalytics pointer id
    const storePointerByCode = new Map(
      storeAnalytic.map((s) => [s?.name, s?.id]).filter(([k, v]) => k && v)
    );

    // ---------- 2) Date window (PH 05:00 → next day 04:59:59.999) ----------
    const { start, end } = getPHWindow_5to459();
    const query = {
      where: {
        tran_date: { $gte: fmtDB(start), $lte: fmtDB(end) },
        store_code: { $in: storeCodes },
        status: "V",
      },
    };

    // ---------- 3) Fetch transactions ----------
    const transactions = await this.findObjectUseCase.execute(
      collection,
      query
    );

    console.log("Transaction: ", transactions);
    if (!transactions?.length) {
      this.view.hideProgressDialog();
      return [];
    }

    // ---------- 4) Collect unique item codes and bulk-fetch products ----------
    const allCodes = new Set();
    for (const trx of transactions) {
      for (const it of trx.items ?? []) {
        const code = safeStr(it?.code);
        if (code) allCodes.add(code);
      }
    }
    const codeList = Array.from(allCodes);
    let products = [];
    if (codeList.length) {
      products = await this.findObjectUseCase.execute("products", {
        where: { sku: { $in: codeList } },
        limit: codeList.length,
      });
    }
    console.log("Products: ", products);
    const productBySku = new Map(
      (products ?? []).map((p) => [safeStr(p?.sku), p])
    );

    console.log("Products By SKU: ".productBySku);

    // ---------- 5) Build waste_records payloads ----------
    const wastePayloads = [];

    console.log("Waste Payloads:", wastePayloads);

    for (const trx of transactions) {
      console.log("trx", trx);
      const items = Array.isArray(trx?.items) ? trx.items : [];
      const store = { id: userLoggedIn.storeAnalytic[0]?.id };
      console.log("store", store);

      const orNumber = safeStr(trx?.tran_id);
      const trandId = safeStr(trx?.id);
      const tranDate = safeStr(trx?.tran_date);
      const status = safeStr(trx?.status);
      const voidDate = (() => {
        const d = new Date(trx?.updated || Date.now()); // per your spec: use transaction.updated
        return isNaN(d.getTime()) ? new Date() : d;
      })();

      for (const it of items) {
        const code = safeStr(it?.code);
        if (!code) continue;

        const product = productBySku.get(code);
        if (!product) continue;

        // --- PRODUCT record (whole object) ---
        const productQty = num(it?.quantity, 1);
        wastePayloads.push({
          orNumber,
          sku: safeStr(product?.sku),
          name: safeStr(product?.itemName || product?.name || product?.sku),
          quantity: String(productQty),
          amount: toMoney(product?.cost),
          voidDate,
          status,
          category: "product",
          storeId: store,
          transactionId: trandId,
          tran_date: tranDate,
          trx_object: trx,
          isUpsale: false,
        });

        // --- COMPONENTS records ---
        const comps = Array.isArray(product?.components)
          ? product.components
          : [];
        for (const c of comps) {
          wastePayloads.push({
            orNumber,
            sku: safeStr(c?.sku),
            name: safeStr(c?.itemName || c?.name || c?.sku),
            quantity: String(num(c?.quantity, 0)),
            amount: toMoney(c?.cost),
            voidDate,
            status,
            category: "components",
            storeId: store,
            transactionId: trandId,
            tran_date: tranDate,
            trx_object: trx,
            isUpsale: false,
          });
        }

        // --- RAW MATERIALS records (raw_materials + others_raw_materials) ---
        const raws = [
          ...(Array.isArray(product?.raw_materials)
            ? product.raw_materials
            : []),
          ...(Array.isArray(product?.others_raw_materials)
            ? product.others_raw_materials
            : []),
        ];
        for (const r of raws) {
          wastePayloads.push({
            orNumber,
            sku: safeStr(r?.sku),
            name: safeStr(r?.itemName || r?.name || r?.sku),
            quantity: String(num(r?.quantity, 0)),
            amount: toMoney(r?.cost),
            voidDate,
            status,
            category: "raw_materials", // unified category
            storeId: store,
            transactionId: trandId,
            tran_date: tranDate,
            trx_object: trx,
            isUpsale: false,
          });
        }
      }
    }

    if (!wastePayloads.length) {
      this.view.hideProgressDialog();
      return transactions;
    }

    const findExistingTrx = await this.findObjectUseCase.execute(
      "waste_records",
      { transactionId: wastePayloads[0]?.transactionId }
    );

    console.log("find", findExistingTrx);

    if (findExistingTrx <= 0) {
      this.view.hideProgressDialog();
      this.view.showProgressDialog();
      console.log("close");
      for (const waste of wastePayloads) {
        console.log("waste", waste);
        await this.upsertUseCase.execute("waste_records", waste);
      }
      this.getObjects();
    }
    this.view.hideProgressDialog();
  }

  async findObjects() {
    const collection = this.view.getCollectionName();
    const query = this.createQuery();
    try {
      this.showProgress();
      this.findObjectUseCase.abort();
      const objects = await this.findObjectUseCase.execute(collection, query);
      this.objects = this.objects.concat(objects);
      console.log("this.object", this.objects);
      this.view.setObjects(this.objects);
      this.view.setCount(this.count);
      this.hideProgress();
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
    this.progress = false;
  }

  onChangeDate(where) {
    const { created } = where;

    if (!created || !created.$gte || !created.$lte) {
      this.filterDate = {};
      this.reset();
      this.getObjects();
      return;
    }

    const startDate = addHours(startOfDay(parseISO(created.$gte)), 5);
    const endDate = addMinutes(
      addHours(addDays(startOfDay(parseISO(created.$lte)), 0), 4),
      59
    );

    this.filterDate = {
      requestDate: {
        $gte: startDate,
        $lte: endDate,
      },
    };

    this.getObjects();
  }

  onClickAdd() {
    this.view.navigateTo("/collection/stock_request/form");
  }

  onClickView(index) {
    const object = this.objects[index];
    this.view.navigateTo(
      "/collection/waste_product/form/" + object.id + "/view"
    );
  }

  async onClickDelete(index) {
    dialog.close();
    const object = this.objects[index];
    const collection = this.view.getCollectionName();

    await this.deleteObjectUseCase.execute(collection, object.id);
    const indexes = this.objects.indexOf(object.id);
    this.objects.splice(indexes, 1);
    this.view.setObjects(this.objects);
    dialog.fire({
      html: (
        <DialogModal
          icon="bi bi-question-octagon-fill"
          iconColor="#82c14a"
          title="Deleted Successfully"
          message=""
          isCancelButton={false}
          onSubmit={() => {
            dialog.close();
          }}
          onClose={() => dialog.close()}
        />
      ),
      footer: false,
    });
  }

  async onCancelSubmit(index) {
    const collection = this.view.getCollectionName();

    const object = this.objects[index];

    const updatePayload = {
      id: object.id,
      status: "Cancelled",
    };

    console.log("object", updatePayload);
    await this.upsertUseCase.execute(collection, updatePayload);
    this.getObjects();
    dialog.close();
  }

  onReceiveAll(index) {
    const object = this.view.state.objects[index];
    this.view.navigateTo(
      "/collection/stock_request/form/" + object.id + "/receive"
    );
    dialog.close();
  }

  onPartialReceive(index) {
    const object = this.view.state.objects[index];
    this.view.navigateTo(
      "/collection/stock_request/form/" + object.id + "/partial"
    );
    dialog.close();
  }

  async upsale(object) {
    dialog.close();
    this.view.showProgressDialog();
    const collection = this.view.getCollectionName();

    try {
      const response = await this.findObjectUseCase.execute(collection, {
        transactionId: object?.transactionId,
      });

      for (const trx of response) {
        trx.isUpsale = true;
        await this.upsertUseCase.execute(collection, trx);
      }
      this.view.hideProgressDialog();
      dialog.fire({
        html: (
          <DialogModal
            icon="bi bi-question-octagon-fill"
            iconColor="#82c14a"
            title="Confirm to Proceed"
            message="Are you sure want to Upsale this Product?"
            isCancelButton={false}
            onSubmit={() => {
              this.getObjects();
              dialog.close();
            }}
            onClose={() => dialog.close()}
          />
        ),
        footer: false,
      });
      // dialog.close();
    } catch (error) {
      throw error;
    }
  }

  onClickUpsale(object) {
    dialog.fire({
      html: (
        <DialogModal
          icon="bi bi-question-octagon-fill"
          iconColor="#82c14a"
          title="Confirm to Proceed"
          message="Are you sure want to Upsale this Product?"
          isCancelButton={true}
          onSubmit={() => this.upsale(object)}
          onClose={() => dialog.close()}
        />
      ),
      footer: false,
    });
  }
  onClickImport() {}
  onClickExport() {}
}

export default WasteProductPresenter;
